import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';

export default function PregnancyStatusDialog({ open, onOpenChange, pregnancy, allCattle, onComplete, onAbort }) {
  const [action, setAction] = useState('complete'); // 'complete' | 'abort'
  const [birthData, setBirthData] = useState({
    date: new Date().toISOString().split('T')[0],
    calfId: 'none',
    calfGender: 'Macho',
    birthWeight: '',
    complications: ''
  });
  const [abortionData, setAbortionData] = useState({
    date: new Date().toISOString().split('T')[0],
    reason: ''
  });

  const handleSubmit = () => {
    if (action === 'complete') {
      if (!birthData.date) {
        toast.error('Debe indicar la fecha del parto');
        return;
      }
      // Convert "none" to empty string
      const finalBirthData = {
        ...birthData,
        calfId: birthData.calfId === 'none' ? null : birthData.calfId
      };
      onComplete(finalBirthData);
    } else {
      if (!abortionData.reason) {
        toast.error('Debe indicar la razón del aborto');
        return;
      }
      onAbort(abortionData);
    }
    
    onOpenChange(false);
    
    // Reset
    setBirthData({
      date: new Date().toISOString().split('T')[0],
      calfId: 'none',
      calfGender: 'Macho',
      birthWeight: '',
      complications: ''
    });
    setAbortionData({
      date: new Date().toISOString().split('T')[0],
      reason: ''
    });
  };

  const availableCalves = allCattle?.filter(c => 
    !c.birthDate || new Date().getFullYear() - new Date(c.birthDate).getFullYear() < 1
  ) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Actualizar Estado del Embarazo</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Selección de Acción */}
          <div className="space-y-3">
            <Label>¿Qué desea registrar?</Label>
            <RadioGroup value={action} onValueChange={setAction}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="complete" id="complete" />
                <Label htmlFor="complete" className="font-normal cursor-pointer">
                  Parto Completado - La vaca ya parió
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="abort" id="abort" />
                <Label htmlFor="abort" className="font-normal cursor-pointer">
                  Aborto - El embarazo se interrumpió
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Formulario de Parto Completado */}
          {action === 'complete' && (
            <div className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <Label>Fecha del Parto</Label>
                <Input
                  type="date"
                  value={birthData.date}
                  onChange={(e) => setBirthData(prev => ({ ...prev, date: e.target.value }))}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="space-y-2">
                <Label>Vincular con Cría Registrada (Opcional)</Label>
                <Select 
                  value={birthData.calfId} 
                  onValueChange={(value) => setBirthData(prev => ({ ...prev, calfId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar cría..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguna - Registrar más tarde</SelectItem>
                    {availableCalves.map(calf => (
                      <SelectItem key={calf.id} value={calf.id}>
                        {calf.name} ({calf.tag})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Si la cría aún no está registrada, puede vincularse después
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Género de la Cría</Label>
                  <Select 
                    value={birthData.calfGender} 
                    onValueChange={(value) => setBirthData(prev => ({ ...prev, calfGender: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Macho">Macho</SelectItem>
                      <SelectItem value="Hembra">Hembra</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Peso al Nacer (kg) - Opcional</Label>
                  <Input
                    type="number"
                    placeholder="Ej: 35"
                    value={birthData.birthWeight}
                    onChange={(e) => setBirthData(prev => ({ ...prev, birthWeight: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Complicaciones (Opcional)</Label>
                <Textarea
                  placeholder="Describe cualquier complicación durante el parto..."
                  value={birthData.complications}
                  onChange={(e) => setBirthData(prev => ({ ...prev, complications: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Formulario de Aborto */}
          {action === 'abort' && (
            <div className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <Label>Fecha del Aborto</Label>
                <Input
                  type="date"
                  value={abortionData.date}
                  onChange={(e) => setAbortionData(prev => ({ ...prev, date: e.target.value }))}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="space-y-2">
                <Label>Razón/Observaciones del Aborto</Label>
                <Textarea
                  placeholder="Describe las circunstancias o causas del aborto..."
                  value={abortionData.reason}
                  onChange={(e) => setAbortionData(prev => ({ ...prev, reason: e.target.value }))}
                  rows={4}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit}
            className={action === 'complete' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-orange-600 hover:bg-orange-700'}
          >
            {action === 'complete' ? 'Registrar Parto' : 'Registrar Aborto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
